import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useState } from "react";
import { useDebouncedMutation } from "@/shared/lib/debounce/debounce";
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import t from "@/lang/lang";
import { Label } from "@/shared/components/ui/label";
import { CompanySelect } from "@/services/backend/company/components/company-select";
import {
  inputValidationErrorMessages,
  InputValidationErrors,
} from "@/shared/components/errors/input-error-messages";
import { TextInput } from "@/shared/components/form/text-input";
import { TextareaInput } from "@/shared/components/form/textarea-input";
import {
  Table,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { Edit, GripVertical, Plus, Trash2 } from "lucide-react";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { v4 } from "uuid";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { CustomerSelect } from "@/services/backend/customers/components/customer-select";
import { NullUUID } from "@bitory-gmbh/nidavellir";
import { useTextblocksCatalogPutMutation } from "../api/catalog-api";
import { Catalog, CatalogItem, orderItems } from "../types/catalog";
import { DeleteCatalogsDialog as DeleteCatalogDialog } from "./delete-catalog-dialog";

export function EditCatalogCard({
  catalog,
  onDeleted,
}: {
  catalog: Catalog;
  onDeleted: () => void;
}) {
  const [request, setRequest] = useState<Catalog>(catalog);
  const mutation = useTextblocksCatalogPutMutation();
  const { error } = useDebouncedMutation(
    request,
    setRequest,
    mutation,
    catalog,
  );
  const [selected, setSelected] = useState<CatalogItem[]>([]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t("Katalog:")} {catalog.name}
        </CardTitle>
        <CardAction>
          <div className="flex items-center gap-2">
            <PersistenceStateUsingContext />
            <DeleteCatalogDialog catalog={catalog} onDeleted={onDeleted} />
          </div>
        </CardAction>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4">
          <div className="grid gap-4 md:grid-cols-3">
            <TextInput
              label={t("Name")}
              field="name"
              text={request.name}
              onTextChange={(name) => setRequest({ ...request, name })}
              errors={inputValidationErrorMessages(error, "name")}
            />
            <div className="grid content-start gap-1.5">
              <Label>{t("Unternehmen")}</Label>
              <CompanySelect
                companyId={request.companyId}
                onCompanyIdChange={(companyId) =>
                  setRequest({ ...request, companyId })
                }
              />
              <InputValidationErrors error={error} field="companyId" />
            </div>
            <div className="grid content-start gap-1.5">
              <Label>{t("Kunde (optional)")}</Label>
              <CustomerSelect
                value={request.customerId}
                onValueChange={(customerId) => {
                  if (customerId === NullUUID) {
                    setRequest({ ...request, customerId: undefined });
                    return;
                  }
                  setRequest({ ...request, customerId });
                }}
                showUnsetOption
              />
              <InputValidationErrors error={error} field="customerId" />
            </div>
          </div>
          <TextareaInput
            label={t("Beschreibung")}
            field="description"
            text={request.description}
            onTextChange={(description) =>
              setRequest({ ...request, description })
            }
            errors={inputValidationErrorMessages(error, "description")}
          />
          <div className="flex justify-end gap-2">
            <DeleteItemsDialog
              items={request.items}
              setItems={(items) => setRequest({ ...request, items })}
              selected={selected}
            />
            <CreateItemDialog
              onItemCreate={(item) =>
                setRequest({
                  ...request,
                  items: [...request.items, item],
                })
              }
              catalogId={catalog.id}
              items={request.items}
            />
          </div>
          <ItemTable
            items={request.items}
            setItems={(items) => setRequest({ ...request, items })}
            selected={selected}
            setSelected={setSelected}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function ItemTable({
  items,
  setItems,
  selected,
  setSelected,
}: {
  items: CatalogItem[];
  setItems: (items: CatalogItem[]) => void;
  selected: CatalogItem[];
  setSelected: (selected: CatalogItem[]) => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);

      const newItems = orderItems(items, oldIndex, newIndex);
      setItems(newItems);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items.map((i) => i.id)}
        strategy={verticalListSortingStrategy}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead />
              <TableHead>
                <Checkbox
                  checked={
                    items.length === selected.length &&
                    items.every((r: CatalogItem) =>
                      selected.some((s) => s.id === r.id),
                    )
                  }
                  onCheckedChange={(value) => {
                    if (value) {
                      setSelected(items);
                    } else {
                      setSelected([]);
                    }
                  }}
                />
              </TableHead>
              <TableHead>{t("Kürzel")}</TableHead>
              <TableHead>{t("Titel")}</TableHead>
              <TableHead>{t("Text")}</TableHead>
              <TableHead className="text-right">{t("Aktionen")}</TableHead>
            </TableRow>
          </TableHeader>
          {items.length === 0 && <TableEmptyRow colSpan={6} />}
          {items.length > 0 &&
            items.map((item) => (
              <ItemRow
                key={item.id}
                item={item}
                items={items}
                setItems={setItems}
                selected={selected.some((r) => r.id === item.id)}
                setSelected={(value) => {
                  if (value) {
                    setSelected([...selected, item]);
                  } else {
                    setSelected(selected.filter((r) => r.id !== item.id));
                  }
                }}
              />
            ))}
        </Table>
      </SortableContext>
    </DndContext>
  );
}

function ItemRow({
  item,
  items,
  setItems,
  selected,
  setSelected,
}: {
  item: CatalogItem;
  items: CatalogItem[];
  setItems: (items: CatalogItem[]) => void;
  selected: boolean;
  setSelected: (selected: boolean) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TableRow ref={setNodeRef} style={style}>
      <TableCell className="w-10">
        <button
          {...attributes}
          {...listeners}
          type="button"
          className="hover:bg-muted cursor-grab rounded p-1"
        >
          <GripVertical className="text-muted-foreground h-4 w-4" />
        </button>
      </TableCell>
      <TableCell>
        <Checkbox checked={selected} onCheckedChange={setSelected} />
      </TableCell>
      <TableCell>{item.shorthand}</TableCell>
      <TableCell>{item.title}</TableCell>
      <TableCell className="max-w-md truncate">{item.text}</TableCell>
      <TableCell>
        <div className="flex justify-end gap-2">
          <EditItemDialog item={item} items={items} setItems={setItems} />
          <DeleteItemsDialog
            items={items}
            setItems={setItems}
            selected={[item]}
          />
        </div>
      </TableCell>
    </TableRow>
  );
}

function CreateItemDialog({
  onItemCreate,
  items,
  catalogId,
}: {
  onItemCreate: (item: CatalogItem) => void;
  catalogId: string;
  items: CatalogItem[];
}) {
  const [open, setOpen] = useState(false);
  const [item, setItem] = useState<CatalogItem>({
    id: v4(),
    catalogId,
    order: 0,
    title: "",
    shorthand: "",
    text: "",
  });

  const doCreate = () => {
    onItemCreate({
      ...item,
      order: items.length,
    });
    setItem({
      id: v4(),
      catalogId,
      order: 0,
      title: "",
      shorthand: "",
      text: "",
    });
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus />
          <span>{t("Neue Bemerkung")}</span>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Neues Bemerkung erstellen")}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <TextInput
            label={t("Kürzel (optional)")}
            field="shorthand"
            text={item.shorthand}
            onTextChange={(shorthand) => setItem({ ...item, shorthand })}
            errors={[]}
          />
          <TextInput
            label={t("Titel (optional)")}
            field="title"
            text={item.title}
            onTextChange={(title) => setItem({ ...item, title })}
            errors={[]}
          />
          <TextareaInput
            label={t("Text")}
            field="text"
            text={item.text}
            onTextChange={(text) => setItem({ ...item, text })}
            errors={[]}
          />
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Abbrechen")}</Button>
          </DialogClose>
          <Button onClick={doCreate}>{t("Erstellen")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteItemsDialog({
  items,
  setItems,
  selected,
}: {
  items: CatalogItem[];
  setItems: (items: CatalogItem[]) => void;
  selected: CatalogItem[];
}) {
  const [open, setOpen] = useState(false);

  const doDelete = () => {
    const newItems = items.filter(
      (item) => !selected.some((s) => s.id === item.id),
    );
    setItems(newItems);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="destructive"
          disabled={selected.length === 0}
          size="sm"
        >
          <Trash2 />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Bemerkungen löschen")}</DialogTitle>
          <DialogDescription>
            {t(
              `Möchten Sie ${selected.length} ausgewählte Bemerkung(en) löschen?`,
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Abbrechen")}</Button>
          </DialogClose>
          <Button onClick={doDelete}>{t("Erstellen")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditItemDialog({
  item,
  items,
  setItems,
}: {
  item: CatalogItem;
  items: CatalogItem[];
  setItems: (items: CatalogItem[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [itemState, setItem] = useState<CatalogItem>(item);

  const doUpdate = () => {
    const newItems = items.map((i) => (i.id === itemState.id ? itemState : i));
    setItems(newItems);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          <Edit />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Bemerkung bearbeiten")}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <TextInput
            label={t("Kürzel (optional)")}
            field="shorthand"
            text={itemState.shorthand}
            onTextChange={(shorthand) => setItem({ ...itemState, shorthand })}
            errors={[]}
          />
          <TextInput
            label={t("Titel (optional)")}
            field="title"
            text={itemState.title}
            onTextChange={(title) => setItem({ ...itemState, title })}
            errors={[]}
          />
          <TextareaInput
            label={t("Text")}
            field="text"
            text={itemState.text}
            onTextChange={(text) => setItem({ ...itemState, text })}
            errors={[]}
          />
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Abbrechen")}</Button>
          </DialogClose>
          <Button onClick={doUpdate}>{t("Aktualisieren")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
